import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, Button } from '@roger/ui-components';
import { AlertTriangle, Plus } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import CodingSearchForm, {
  CodingSearchParams,
} from '../components/CodingSearchForm';
import { searchCodingVisits, CodingVisit } from '../services/rogerDocApi';
import NewVisitDialog from '../components/NewVisitDialog';
import { useCodingSearchParams } from '../hooks/useCodingSearchParams';

/*
  TODO: Enable and delete this flag when we want to use ROG-1369 (Coding non-Kinnser agencies)
  This button allows user to manually upload the inputs for coding and run it on-demand without
  choosing a visit normally from the selector.
  The UI is unfinalized and prob should be based on some `!Agency.uses_kinnser_emr` type flag
*/
const ENABLE_NEW_VISIT_FEATURE = false;

const CodingSearch: React.FC = () => {
  const { showError, hasPermission } = useStore(
    useShallow(state => ({
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  const navigate = useNavigate();

  // URL-synced search params (nuqs is the single source of truth)
  const { searchParams, setSearchParams, clearSearchParams } =
    useCodingSearchParams();

  // Other state (not URL-synced)
  const [searchResults, setSearchResults] = useState<CodingVisit[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const initialSearchTriggered = useRef(false);

  // Check if user has permission to access Coding
  if (!hasPermission('coding:access')) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            You don't have permission to access Medical Coding features. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const [showNewVisitDialog, setShowNewVisitDialog] = useState(false);

  // Helper function to format search parameters for API
  const formatSearchParamsForAPI = (params: CodingSearchParams) => {
    const apiParams: Record<
      string,
      string | string[] | Record<string, boolean>
    > = {};

    if (params.phrase.trim()) {
      apiParams.phrase = params.phrase.trim();
    }

    if (params.start_date) {
      apiParams.start_date = params.start_date.toISOString().split('T')[0];
    }

    if (params.end_date) {
      apiParams.end_date = params.end_date.toISOString().split('T')[0];
    }

    if (params.coding_status.length > 0) {
      apiParams.coding_status = params.coding_status;
    }

    if (params.visit_types.length > 0) {
      apiParams.visit_types = params.visit_types;
    }

    if (params.clinics.length > 0) {
      apiParams.clinics = params.clinics;
    }

    if (params.tags && Object.keys(params.tags).length > 0) {
      apiParams.tags = params.tags;
    }

    return apiParams;
  };

  const handleClearFilters = () => {
    clearSearchParams();
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleSearch = async (params: CodingSearchParams) => {
    // Update URL params (single source of truth)
    setSearchParams(params);

    setSearching(true);
    setSearchResults([]);
    setHasSearched(false);

    try {
      const apiParams = formatSearchParamsForAPI(params);
      const results = await searchCodingVisits(apiParams);
      setSearchResults(results);
      setHasSearched(true);
    } catch (error: unknown) {
      console.error('Error searching for visits:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to search for visits';
      showError(message);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  const handleVisitSelected = (visit: CodingVisit) => {
    navigate(`/coding/${visit.diagnosis_id}`, {
      state: { search: window.location.search },
    });
  };

  // Automatically search on component mount
  useEffect(() => {
    if (!initialSearchTriggered.current) {
      initialSearchTriggered.current = true;
      handleSearch(searchParams);
    }
  }, []);

  return (
    <div className='container mx-auto px-4 py-6'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-2xl font-bold tracking-tight'>Medical Coding</h1>
        {ENABLE_NEW_VISIT_FEATURE && (
          <Button onClick={() => setShowNewVisitDialog(true)}>
            <Plus className='h-4 w-4 mr-2' />
            New Visit
          </Button>
        )}
      </div>

      {ENABLE_NEW_VISIT_FEATURE && (
        <NewVisitDialog
          open={showNewVisitDialog}
          onOpenChange={setShowNewVisitDialog}
          onVisitCreated={() => handleSearch(searchParams)}
        />
      )}

      <CodingSearchForm
        searchParams={searchParams}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        searching={searching}
        searchResults={searchResults}
        hasSearched={hasSearched}
        selectedVisit={null}
        loading={false}
        onVisitSelected={handleVisitSelected}
        onChangeSelection={() => {}}
      />
    </div>
  );
};

export default CodingSearch;
